// Wave 9 (port-plan 22, prototype 7d1b56f) — compact DialPad overview
// volume card. Consolidates the Teams dashboard's three stacked
// full-height charts (Daily / Hourly / Weekday call volume) into ONE
// card with a segmented toggle: the active grouping renders in a single
// shorter chart. Every mode keeps the behaviour the stacked charts had —
// the prior-period hatched overlay, the funnel/sentiment tile filters,
// the stable y-axis scaling, and the per-bar tooltips.
//
// The three per-mode builders below carry the mode-specific logic
// (bucket backfill, prior pairing, tooltip copy, axis labels) that used
// to live in the three separate components in TeamsDashboardView.tsx;
// the shared bar/overlay/label DOM is rendered once by VolumeBarChart.
import { useState, type CSSProperties } from "react";
import { Tooltip } from "@/components/Tooltip";
import { rangeTitle } from "@/components/TimeRangeControls.helpers";

import type {
  AnalyticsDailyPoint,
  AnalyticsHourlyPoint,
  AnalyticsWeekdayPoint,
  RangeMode,
  TimeRange,
} from "@/lib/api";

// Tile-filter types shared with the clickable MetricCard KPI strip in
// TeamsDashboardView (which owns the filter state and passes it down).
export type FunnelFilter = "registered" | "viable" | "quality" | null;
export type SentimentFilter = "positive" | "negative" | null;

export type VolumeMode = "daily" | "hourly" | "weekday";

type SeriesKey = "registered" | "viable" | "quality" | "positive" | "negative";

const VOLUME_SERIES: {
  key: SeriesKey;
  totalKey: `${SeriesKey}_total`;
  label: string;
  color: string;
  group: "funnel" | "sentiment";
}[] = [
  { key: "registered", totalKey: "registered_total", label: "Registered", color: "bg-muted-foreground", group: "funnel" },
  { key: "viable",     totalKey: "viable_total",     label: "Viable",     color: "bg-secondary",        group: "funnel" },
  { key: "quality",    totalKey: "quality_total",    label: "Quality",    color: "bg-accent",           group: "funnel" },
  { key: "positive",   totalKey: "positive_total",   label: "Positive",   color: "bg-primary",          group: "sentiment" },
  { key: "negative",   totalKey: "negative_total",   label: "Negative",   color: "bg-destructive",      group: "sentiment" },
];

// Compact height — the point of the consolidation is that the tile fits
// in roughly the space the old tile used before its Hourly chart even
// appeared. (Prototype note: h-40/48/56 rather than the mockup's h-36,
// for bar legibility.)
const CHART_HEIGHT = "h-40 sm:h-48 lg:h-56";

const MODE_TABS: { key: VolumeMode; label: string }[] = [
  { key: "daily", label: "Daily" },
  { key: "hourly", label: "Hourly" },
  { key: "weekday", label: "Weekday" },
];

const MODE_TITLE: Record<VolumeMode, string> = {
  daily: "Daily call volume",
  hourly: "Average call volume by hour of day",
  weekday: "Average call volume by day of week",
};

function localTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "local time";
  } catch {
    return "local time";
  }
}

const fmtAvg = (v: number) =>
  v === 0 ? "0" : v >= 10 ? Math.round(v).toString() : v.toFixed(1);

// One bar cluster's worth of data for a single series within a bucket.
type VolumeBarDatum = {
  value: number;
  prior: number | null;
  tooltip: string;
  priorTooltip: string;
};

type VolumeBucket = {
  key: string | number;
  // Bottom axis label. ``null`` renders the invisible placeholder that
  // keeps column heights aligned when a crowded daily axis skips labels.
  label: string | null;
  bars: Record<SeriesKey, VolumeBarDatum>;
};

// Everything the shared renderer needs that differs per grouping.
type VolumeChartSpec = {
  buckets: VolumeBucket[];
  // Scale against the FULL series set (not just the filtered ones) so
  // the y-axis stays stable when the user toggles tile filters —
  // otherwise the same bar appears to jump in height as the max changes.
  max: number;
  gapStyle: CSSProperties | undefined;
  justifyCenter: boolean;
  columnStyle?: CSSProperties;
  barWrapperStyle?: CSSProperties;
};

function buildDailySpec(
  data: AnalyticsDailyPoint[],
  prior: AnalyticsDailyPoint[] | null,
): VolumeChartSpec {
  // For Daily (1 bar) we show wider bars; otherwise a fixed grid scales
  // down naturally and the chart stays within its container.
  const isSingleDay = data.length <= 1;
  // Show roughly up to ~14 labels across the range so they never overlap.
  const labelEvery = Math.max(1, Math.ceil(data.length / 14));
  const max = Math.max(
    1,
    ...data.flatMap((d) => VOLUME_SERIES.map((s) => d[s.key])),
    ...(prior ?? []).flatMap((d) => VOLUME_SERIES.map((s) => d[s.key])),
  );
  // Pair current with prior by index so day-1 lines up with day-1 of the
  // prior period, regardless of the actual calendar dates.
  const buckets = data.map((d, idx) => {
    const dt = new Date(`${d.date}T00:00:00Z`);
    const dom = dt.getUTCDate();
    const dow = dt.toLocaleDateString(undefined, {
      weekday: "short",
      timeZone: "UTC",
    });
    const priorPoint = prior?.[idx];
    const showLabel =
      isSingleDay ||
      data.length <= 14 ||
      idx % labelEvery === 0 ||
      idx === data.length - 1;
    const bars = {} as Record<SeriesKey, VolumeBarDatum>;
    for (const s of VOLUME_SERIES) {
      const v = d[s.key];
      const pv = priorPoint ? priorPoint[s.key] : null;
      bars[s.key] = {
        value: v,
        prior: pv,
        tooltip: `${s.label}: ${v}`,
        priorTooltip: `${s.label} (prior): ${pv ?? 0}`,
      };
    }
    return {
      key: d.date,
      label: showLabel
        ? isSingleDay || data.length <= 14
          ? `${dow} ${dom}`
          : String(dom)
        : null,
      bars,
    };
  });
  return {
    buckets,
    max,
    gapStyle: isSingleDay
      ? undefined
      : {
          gap:
            data.length > 60 ? "1px" : `clamp(2px, ${24 / data.length}%, 8px)`,
        },
    justifyCenter: isSingleDay,
    columnStyle: isSingleDay ? { maxWidth: "160px" } : undefined,
    barWrapperStyle: isSingleDay ? { maxWidth: "28px" } : undefined,
  };
}

function buildHourlySpec(
  data: AnalyticsHourlyPoint[],
  prior: AnalyticsHourlyPoint[] | null,
): VolumeChartSpec {
  // Always render 24 buckets so empty hours show as empty bars rather
  // than missing slots. Backfill any missing hour from the API just in
  // case (defensive — the server already returns 24 rows).
  const emptyHour = (h: number): AnalyticsHourlyPoint => ({
    hour: h,
    registered: 0, viable: 0, quality: 0, positive: 0, negative: 0,
    registered_total: 0, viable_total: 0, quality_total: 0,
    positive_total: 0, negative_total: 0,
  });
  const byHour = new Map(data.map((d) => [d.hour, d]));
  const hours: AnalyticsHourlyPoint[] = Array.from({ length: 24 }, (_, h) =>
    byHour.get(h) ?? emptyHour(h),
  );
  const priorByHour = prior ? new Map(prior.map((d) => [d.hour, d])) : null;
  const max = Math.max(
    1,
    ...hours.flatMap((h) => VOLUME_SERIES.map((s) => h[s.key])),
    ...(prior ?? []).flatMap((h) => VOLUME_SERIES.map((s) => h[s.key])),
  );
  // Compact hour labels every 3 hours: 12a, 3a, 6a, 9a, 12p, 3p, 6p, 9p.
  const hourLabel = (h: number) => {
    if (h % 3 !== 0) return "";
    const ampm = h < 12 ? "a" : "p";
    const display = h === 0 ? 12 : h <= 12 ? h : h - 12;
    return `${display}${ampm}`;
  };
  const buckets = hours.map((h) => {
    const ph = priorByHour?.get(h.hour) ?? null;
    const bars = {} as Record<SeriesKey, VolumeBarDatum>;
    for (const s of VOLUME_SERIES) {
      const v = h[s.key];
      const total = h[s.totalKey];
      const pv = ph ? ph[s.key] : null;
      const ptotal = ph ? ph[s.totalKey] : 0;
      bars[s.key] = {
        value: v,
        prior: pv,
        tooltip: `${s.label}: avg ${fmtAvg(v)} per hour (total ${total})`,
        priorTooltip: `${s.label} (prior): avg ${fmtAvg(pv ?? 0)} per hour (total ${ptotal})`,
      };
    }
    return { key: h.hour, label: hourLabel(h.hour) || "\u00A0", bars };
  });
  return {
    buckets,
    max,
    gapStyle: { gap: "clamp(2px, 1%, 6px)" },
    justifyCenter: false,
  };
}

function buildWeekdaySpec(
  data: AnalyticsWeekdayPoint[],
  prior: AnalyticsWeekdayPoint[] | null,
): VolumeChartSpec {
  // Mon=0 … Sun=6. Backfill so all 7 days show as empty bars when the
  // window happens not to include one of them (defensive — the server
  // already returns 7 rows in practice).
  const emptyDay = (w: number): AnalyticsWeekdayPoint => ({
    weekday: w,
    registered: 0, viable: 0, quality: 0, positive: 0, negative: 0,
    registered_total: 0, viable_total: 0, quality_total: 0,
    positive_total: 0, negative_total: 0,
  });
  const byDay = new Map(data.map((d) => [d.weekday, d]));
  const days: AnalyticsWeekdayPoint[] = Array.from({ length: 7 }, (_, w) =>
    byDay.get(w) ?? emptyDay(w),
  );
  const priorByDay = prior ? new Map(prior.map((d) => [d.weekday, d])) : null;
  const max = Math.max(
    1,
    ...days.flatMap((d) => VOLUME_SERIES.map((s) => d[s.key])),
    ...(prior ?? []).flatMap((d) => VOLUME_SERIES.map((s) => d[s.key])),
  );
  const dayLabel = (w: number) =>
    ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"][w] ?? "";
  const buckets = days.map((d) => {
    const pd = priorByDay?.get(d.weekday) ?? null;
    const bars = {} as Record<SeriesKey, VolumeBarDatum>;
    for (const s of VOLUME_SERIES) {
      const v = d[s.key];
      const total = d[s.totalKey];
      const pv = pd ? pd[s.key] : null;
      const ptotal = pd ? pd[s.totalKey] : 0;
      bars[s.key] = {
        value: v,
        prior: pv,
        tooltip: `${s.label}: avg ${fmtAvg(v)} per ${dayLabel(d.weekday)} (total ${total})`,
        priorTooltip: `${s.label} (prior): avg ${fmtAvg(pv ?? 0)} per ${dayLabel(d.weekday)} (total ${ptotal})`,
      };
    }
    return { key: d.weekday, label: dayLabel(d.weekday), bars };
  });
  return {
    buckets,
    max,
    gapStyle: { gap: "clamp(4px, 2%, 12px)" },
    justifyCenter: false,
  };
}

// Shared bar renderer — the DOM the three stacked charts used to
// duplicate: per-bucket columns, per-series current bar + optional
// hatched prior-period bar (both tooltipped), and the bottom axis label.
function VolumeBarChart({
  spec,
  series,
  hasPrior,
}: {
  spec: VolumeChartSpec;
  series: typeof VOLUME_SERIES;
  hasPrior: boolean;
}) {
  const { buckets, max, gapStyle, justifyCenter, columnStyle, barWrapperStyle } =
    spec;
  return (
    <div
      className={`flex ${CHART_HEIGHT} items-end w-full ${justifyCenter ? "justify-center" : ""}`}
      style={gapStyle}
    >
      {buckets.map((b) => (
        <div
          key={b.key}
          className="flex flex-col items-center h-full min-w-0 flex-1"
          style={columnStyle}
        >
          <div className="flex-1 w-full flex items-end justify-center gap-px sm:gap-0.5">
            {series.map((s) => {
              const bar = b.bars[s.key];
              const h = (bar.value / max) * 92;
              const ph = bar.prior != null ? (bar.prior / max) * 92 : null;
              return (
                <div
                  key={s.key}
                  className="flex items-end gap-px h-full flex-1 min-w-0 justify-center"
                >
                  <Tooltip
                    content={bar.tooltip}
                    wrapperClassName="flex-1 min-w-0 h-full flex items-end justify-center"
                    wrapperStyle={barWrapperStyle}
                  >
                    <div
                      className={`rounded-t ${s.color} w-full `}
                      style={{ height: `${Math.max(bar.value ? 2 : 0, h)}%` }}
                    />
                  </Tooltip>
                  {hasPrior ? (
                    <Tooltip
                      content={bar.priorTooltip}
                      wrapperClassName="flex-1 min-w-0 h-full flex items-end justify-center"
                      wrapperStyle={barWrapperStyle}
                    >
                      <div
                        className={`rounded-t ${s.color} opacity-40 w-full `}
                        style={{
                          height: `${Math.max((bar.prior ?? 0) ? 2 : 0, ph ?? 0)}%`,
                          backgroundImage:
                            "repeating-linear-gradient(45deg, rgba(255,255,255,0.6) 0 2px, transparent 2px 4px)",
                        }}
                      />
                    </Tooltip>
                  ) : null}
                </div>
              );
            })}
          </div>
          {b.label != null ? (
            <div className="mt-1 text-[10px] text-muted-foreground whitespace-nowrap overflow-hidden">
              {b.label}
            </div>
          ) : (
            <div className="mt-1 text-[10px] text-transparent">.</div>
          )}
        </div>
      ))}
    </div>
  );
}

/**
 * Compact call-volume card with a Daily / Hourly / Weekday segmented
 * toggle (the same toggle-group primitive the period selectors use).
 * Replaces the three stacked full-height charts in the DialPad overview
 * tile — the active grouping renders one shorter chart, and the legend
 * narrows with the active funnel/sentiment tile filters.
 *
 * Mode state is local to the card: which grouping is on screen is a
 * presentation-only choice, unlike the funnel/sentiment filters, which
 * stay owned by the view because the KPI strip drives them.
 */
export function TeamVolumeCard({
  daily,
  priorDaily,
  hourly,
  priorHourly,
  weekday,
  priorWeekday,
  timeRange,
  rangeMode,
  funnelFilter = null,
  sentimentFilter = null,
}: {
  daily: AnalyticsDailyPoint[];
  priorDaily: AnalyticsDailyPoint[] | null;
  hourly: AnalyticsHourlyPoint[];
  priorHourly: AnalyticsHourlyPoint[] | null;
  weekday: AnalyticsWeekdayPoint[];
  priorWeekday: AnalyticsWeekdayPoint[] | null;
  timeRange: TimeRange;
  rangeMode: RangeMode;
  funnelFilter?: FunnelFilter;
  sentimentFilter?: SentimentFilter;
}) {
  const [mode, setMode] = useState<VolumeMode>("daily");
  const series = VOLUME_SERIES.filter((s) =>
    s.group === "funnel"
      ? funnelFilter == null || s.key === funnelFilter
      : sentimentFilter == null || s.key === sentimentFilter,
  );
  const spec =
    mode === "daily"
      ? buildDailySpec(daily, priorDaily)
      : mode === "hourly"
        ? buildHourlySpec(hourly, priorHourly)
        : buildWeekdaySpec(weekday, priorWeekday);
  const hasPrior =
    mode === "daily"
      ? !!priorDaily
      : mode === "hourly"
        ? !!priorHourly
        : !!priorWeekday;
  // Keep the stacked charts' informative captions: the Hourly/Weekday
  // groupings are per-bucket AVERAGES in the viewer's timezone, and the
  // hatched-overlay hint only shows when a prior period is loaded.
  const caption =
    mode === "daily"
      ? hasPrior
        ? "(vs prior period, hatched)"
        : null
      : `(per-${mode === "hourly" ? "hour" : "weekday"} averages, ${localTimeZone()}${
          hasPrior ? "; vs prior period, hatched" : ""
        })`;
  return (
    <div className="rounded-lg border border-border bg-card p-4">
      <div className="mb-3 flex items-center justify-between gap-2 flex-wrap">
        <h3 className="text-sm font-semibold text-foreground">
          {MODE_TITLE[mode]} — {rangeTitle(timeRange, rangeMode)}
          {caption ? (
            <span className="ml-1 text-xs font-normal text-muted-foreground">
              {caption}
            </span>
          ) : null}
        </h3>
        <div
          role="group"
          aria-label="Volume grouping"
          className="inline-flex rounded-md border border-input bg-card overflow-hidden"
        >
          {MODE_TABS.map((t) => {
            const active = t.key === mode;
            return (
              <button
                key={t.key}
                type="button"
                onClick={() => setMode(t.key)}
                className={`px-3 py-1.5 text-sm border-r border-border last:border-r-0 ${
                  active
                    ? "bg-primary text-primary-foreground"
                    : "text-foreground hover:bg-muted/40"
                }`}
                aria-pressed={active}
              >
                {t.label}
              </button>
            );
          })}
        </div>
      </div>
      <VolumeBarChart spec={spec} series={series} hasPrior={hasPrior} />
      <div className="mt-3 flex items-center gap-3 text-xs text-muted-foreground flex-wrap">
        {series.map((s) => (
          <span key={s.key} className="inline-flex items-center gap-1">
            <span className={`inline-block h-2 w-2 rounded-sm ${s.color}`} />
            {s.label}
          </span>
        ))}
      </div>
    </div>
  );
}
